import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Mail,
  Settings,
  Edit3,
  Send,
  Save,
  X,
  FileText,
} from 'lucide-react';
import { notificationsApi } from '@/api/endpoints';
import { toast } from '@/utils/toast';

interface NotificationTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  type: string;
  is_active: boolean;
  variables: string[];
  updated_at: string;
}

interface NotificationSettingsMap {
  [key: string]: string | boolean | number;
}

export const NotificationAdminPage = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'templates' | 'settings'>('templates');

  // Templates state
  const [editModal, setEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editActive, setEditActive] = useState(true);

  // Test email state
  const [testEmailModal, setTestEmailModal] = useState(false);
  const [testTemplateId, setTestTemplateId] = useState('');
  const [testEmail, setTestEmail] = useState('');

  // Settings state
  const [settingsEdited, setSettingsEdited] = useState<NotificationSettingsMap>({});
  const [hasSettingsChanges, setHasSettingsChanges] = useState(false);

  // Fetch templates
  const { data: templatesRes, isLoading: templatesLoading } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () => notificationsApi.getTemplates(),
    staleTime: 30000,
  });

  const templates: NotificationTemplate[] = Array.isArray(templatesRes?.data?.data)
    ? (templatesRes.data.data as NotificationTemplate[])
    : [];

  // Fetch settings
  const { data: settingsRes, isLoading: settingsLoading } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => notificationsApi.getSettings(),
    staleTime: 30000,
  });

  const rawSettings: NotificationSettingsMap = (settingsRes?.data?.data || {}) as NotificationSettingsMap;
  const settings: NotificationSettingsMap = hasSettingsChanges
    ? { ...rawSettings, ...settingsEdited }
    : rawSettings;

  // Update template mutation
  const updateTemplateMutation = useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: unknown }) =>
      notificationsApi.updateTemplate(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
      toast.success('Template updated');
      setEditModal(false);
      setEditingTemplate(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to update template'),
  });

  // Send test email mutation
  const sendTestMutation = useMutation({
    mutationFn: ({ templateId, email }: { templateId: string; email: string }) =>
      notificationsApi.sendTestEmail(templateId, email),
    onSuccess: () => {
      toast.success('Test email sent');
      setTestEmailModal(false);
      setTestTemplateId('');
      setTestEmail('');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to send test email'),
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (updatedSettings: unknown) => notificationsApi.updateSettings(updatedSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      toast.success('Settings saved');
      setHasSettingsChanges(false);
      setSettingsEdited({});
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to save settings'),
  });

  const openEditModal = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setEditSubject(template.subject || '');
    setEditBody(template.body || '');
    setEditActive(template.is_active !== false);
    setEditModal(true);
  };

  const handleSaveTemplate = () => {
    if (!editingTemplate) return;
    updateTemplateMutation.mutate({
      templateId: editingTemplate.id,
      data: {
        subject: editSubject,
        body: editBody,
        is_active: editActive,
      },
    });
  };

  const openTestEmail = (templateId: string) => {
    setTestTemplateId(templateId);
    setTestEmail('');
    setTestEmailModal(true);
  };

  const handleSendTest = () => {
    if (!testTemplateId || !testEmail) return;
    sendTestMutation.mutate({ templateId: testTemplateId, email: testEmail });
  };

  const handleSettingChange = (key: string, value: string) => {
    setSettingsEdited((prev) => ({ ...prev, [key]: value }));
    setHasSettingsChanges(true);
  };

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate({ ...rawSettings, ...settingsEdited });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Notification Management</h1>
          <p className="text-sm text-white/50 mt-1">Manage email templates and notification settings</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {(['templates', 'settings'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === tab
                ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                : 'text-white/50 hover:text-white/70 hover:bg-white/[0.04]'
            }`}
          >
            {tab === 'templates' ? <FileText className="w-4 h-4" /> : <Settings className="w-4 h-4" />}
            <span className="capitalize">{tab}</span>
          </button>
        ))}
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div>
          {templatesLoading ? (
            <div className="text-center text-white/50 py-8">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="card-dark p-8 text-center">
              <Bell className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/60">No notification templates found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <div key={template.id} className="card-dark rounded-xl p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-teal-400" />
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        template.is_active !== false
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-white/[0.06] text-white/40'
                      }`}
                    >
                      {template.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-white/90 mb-1">{template.name}</h3>
                  <p className="text-xs text-white/50 mb-1">Type: {template.type || 'email'}</p>
                  <p className="text-xs text-white/40 mb-4 line-clamp-2">
                    {template.subject || 'No subject'}
                  </p>
                  {template.variables && template.variables.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {template.variables.slice(0, 4).map((v) => (
                        <span key={v} className="px-1.5 py-0.5 rounded bg-white/[0.06] text-xs text-white/50">
                          {`{{${v}}}`}
                        </span>
                      ))}
                      {template.variables.length > 4 && (
                        <span className="text-xs text-white/40">+{template.variables.length - 4} more</span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(template)}
                      className="btn-outline flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => openTestEmail(template.id)}
                      className="btn-primary flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm"
                    >
                      <Send className="w-3.5 h-3.5" />
                      <span>Send Test</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          {settingsLoading ? (
            <div className="text-center text-white/50 py-8">Loading settings...</div>
          ) : Object.keys(rawSettings).length === 0 ? (
            <div className="card-dark p-8 text-center">
              <Settings className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/60">No notification settings configured</p>
            </div>
          ) : (
            <div className="card-dark rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white/90">Notification Settings</h3>
                <button
                  onClick={handleSaveSettings}
                  disabled={!hasSettingsChanges || updateSettingsMutation.isPending}
                  className="btn-primary flex items-center gap-2 px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{updateSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
              <div className="space-y-4">
                {Object.entries(settings).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-4 border-b border-white/[0.06] pb-4 last:border-0">
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm font-medium text-white/70 mb-1">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </label>
                      <p className="text-xs text-white/40">{key}</p>
                    </div>
                    <div className="w-72">
                      <input
                        type="text"
                        value={String(settingsEdited[key] !== undefined ? settingsEdited[key] : value)}
                        onChange={(e) => handleSettingChange(key, e.target.value)}
                        className="input-dark w-full text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Template Modal */}
      {editModal && editingTemplate && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setEditModal(false)}
        >
          <div
            className="bg-[#1a1f2e] border border-white/[0.08] rounded-xl p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white/90">Edit Template</h3>
              <button onClick={() => setEditModal(false)} className="text-white/40 hover:text-white/70">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-1">Template Name</label>
                <input
                  type="text"
                  value={editingTemplate.name}
                  disabled
                  className="input-dark w-full text-sm opacity-60"
                />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Subject</label>
                <input
                  type="text"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  className="input-dark w-full text-sm"
                  placeholder="Email subject line"
                />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Body</label>
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  className="input-dark w-full text-sm"
                  rows={8}
                  placeholder="Email body content (supports HTML)"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-white/70">Active</label>
                <button
                  type="button"
                  onClick={() => setEditActive(!editActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    editActive ? 'bg-teal-500' : 'bg-white/[0.1]'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="btn-outline px-4 py-2 rounded-lg text-sm"
                onClick={() => setEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                onClick={handleSaveTemplate}
                disabled={updateTemplateMutation.isPending}
              >
                {updateTemplateMutation.isPending ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Test Email Modal */}
      {testEmailModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setTestEmailModal(false)}
        >
          <div
            className="bg-[#1a1f2e] border border-white/[0.08] rounded-xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white/90">Send Test Email</h3>
              <button onClick={() => setTestEmailModal(false)} className="text-white/40 hover:text-white/70">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-1">Recipient Email</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="input-dark w-full text-sm"
                  placeholder="test@example.com"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="btn-outline px-4 py-2 rounded-lg text-sm"
                onClick={() => setTestEmailModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                onClick={handleSendTest}
                disabled={!testEmail || sendTestMutation.isPending}
              >
                {sendTestMutation.isPending ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
